// Utilitário para gerar uma cor legível baseada em um texto (id),
// com garantia de contraste mínimo em relação ao fundo branco.

export function getReadableColorFromText(text: string, minContrast: number = 4.5): string {
  const base = (text || '').toString().trim();
  const hash = hashString(base);
  const hue = Math.abs(hash) % 360;
  let s = 60;
  let l = 45;
  let { r, g, b } = hslToRgb(hue / 360, s / 100, l / 100);
  ({ r, g, b } = ensureContrast({ r, g, b }, { r: 255, g: 255, b: 255 }, minContrast));
  return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
}

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0; // 32-bit
  }
  return hash;
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  if (s === 0) {
    const v = l * 255;
    return { r: v, g: v, b: v };
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = hue2rgb(p, q, h + 1 / 3);
  const g = hue2rgb(p, q, h);
  const b = hue2rgb(p, q, h - 1 / 3);
  return { r: r * 255, g: g * 255, b: b * 255 };
}

function hue2rgb(p: number, q: number, t: number): number {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}

function relativeLuminance(rgb: { r: number; g: number; b: number }): number {
  const srgb = [rgb.r, rgb.g, rgb.b]
    .map(v => v / 255)
    .map(v => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)));
  const [r, g, b] = srgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastRatio(rgb1: { r: number; g: number; b: number }, rgb2: { r: number; g: number; b: number }): number {
  const L1 = relativeLuminance(rgb1);
  const L2 = relativeLuminance(rgb2);
  const [maxL, minL] = L1 > L2 ? [L1, L2] : [L2, L1];
  return (maxL + 0.05) / (minL + 0.05);
}

function ensureContrast(
  rgb: { r: number; g: number; b: number },
  background: { r: number; g: number; b: number },
  minContrast: number
): { r: number; g: number; b: number } {
  let attempt = { ...rgb };
  let ratio = contrastRatio(attempt, background);
  if (ratio >= minContrast) return attempt;
  const maxIterations = 20;
  for (let i = 0; i < maxIterations; i++) {
    attempt = {
      r: attempt.r * 0.9,
      g: attempt.g * 0.9,
      b: attempt.b * 0.9
    };
    ratio = contrastRatio(attempt, background);
    if (ratio >= minContrast) break;
  }
  return attempt;
}
